import Permissions from "../../../utils/permissions";
import AccessDenied from "../../shared/common/accessDenied";
import React, { useEffect, useState } from "react";
import AllowedTo from "../../shared/common/rbac";
import {
  AddIcon,
  ApproveIcon,
  CancelIcon,
  CaretDownSolidIcon,
  EditIcon,
  FilterIcon,
  ResetIcon,
} from "../../../assets/icons";
import Select from "react-select";
import {
  getSubjects,
  ILessonPlan,
  ILessonPlanAsset,
  ILessonPlanSubject,
  addLessonPlan,
  IAddLessonPlanCommand,
  getLessonPlanById,
  IUpdateLessonPlanCommand,
  updateLessonPlan,
  getLessonPlanAssestsByLessonPlanId,
} from "../../../api/lessonPlans.service";
import { ISelectElement } from "../../../model/interface/keyValuePair";
import { useNavigate, useParams } from "react-router";
import Loader from "../../shared/loader/loader";
import Constant from "../../../utils/constant";
import { toast } from "react-toastify";
import constant from "../../../utils/constant";
import { IBreadcrumb } from "../../../model/interface/breadcrumb";
import Breadcrumb from "../../shared/common/breadcrumb";

export const LessonPlanEdit = (props: any) => {
  const params = useParams<{ lessonPlanId: string | undefined }>();

  const [breadCrumbs, setBreadCrumbs] = useState<Array<IBreadcrumb>>([]);
  const [subject, setSubject] = useState<ISelectElement>();
  const [subjects, setSubjects] = useState<Array<ISelectElement>>([]);
  const [assets, setAssets] = useState<Array<ILessonPlanAsset>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [lessonPlan, setLessonPlan] = useState<ILessonPlan>({
    lessonPlanId: params.lessonPlanId ? Number(params.lessonPlanId) : 0,
    subjectId: 0,
    subjectName: "",
    isApproved: false,
    assetCount: 0,
  });

  const navigate = useNavigate();

  const breadcrumbItems = () => {
    const items: Array<IBreadcrumb> = [
      {
        name: "Lesson Plans",
        url: constant.LESSONPLANS,
      },
      {
        name: `Lesson Plan (${
          lessonPlan.lessonPlanId === 0 ? "New" : lessonPlan.lessonPlanId
        })`,
        url: "",
      },
    ];

    return items;
  };

  const handleSubjectChange = (event) => {
    setLessonPlan({ ...lessonPlan, subjectId: parseInt(event.value) });
    setSubject(event);
  };

  const handleIsApprovedChange = (event) => {
    setLessonPlan({ ...lessonPlan, isApproved: event.target.checked });
  };

  const handleSaveClick = () => {
    if (lessonPlan.subjectId === 0) return;

    setIsLoading(true);

    if (lessonPlan.lessonPlanId > 0) {
      saveExistingLessonPlan();
    } else {
      createNewLessonPlan();
    }
  };

  const handleCancelClick = () => {
    navigate(Constant.LESSONPLANS);
  };

  const handleAddAssetClick = () => {
    if (lessonPlan.lessonPlanId === 0) return;

    navigate(
      Constant.LESSONPLANS_ASSETS_NEW.replace(
        ":lessonPlanId",
        lessonPlan.lessonPlanId.toString()
      )
    );
  };

  const handleEditAssetclick = (lessonPlanAssetId: number) => {
    if (lessonPlan.lessonPlanId === 0) return;

    navigate(
      Constant.LESSONPLANS_ASSETS_EDIT.replace(
        ":lessonPlanId",
        lessonPlan.lessonPlanId.toString()
      ).replace(":lessonPlanAssetId", lessonPlanAssetId.toString())
    );
  };

  const createNewLessonPlan = () => {
    if (subject === undefined) return;

    const command: IAddLessonPlanCommand = {
      subjectId: parseInt(subject.value),
      isApproved: lessonPlan.isApproved,
    };

    // save new lesson plan, then redirect to lesson plan edit
    addLessonPlan(command)
      .then((response) => {
        setIsLoading(false);
        setLessonPlan({ ...lessonPlan, lessonPlanId: response.data });
        if (response.data) {
          navigate(
            Constant.LESSONPLANS_EDIT.replace(
              ":lessonPlanId",
              response.data.toString()
            )
          );
        } else {
          toast.error("Failed to save lesson plan");
        }
      })
      .catch((error) => {
        setIsLoading(false);
        toast.error("Failed to create lesson plan");
      });
  };

  const saveExistingLessonPlan = () => {
    if (lessonPlan === undefined) return;

    const command: IUpdateLessonPlanCommand = {
      subjectId: lessonPlan?.subjectId,
      isApproved: lessonPlan?.isApproved,
    };

    // save new lesson plan, then redirect to lesson plan edit
    updateLessonPlan(lessonPlan.lessonPlanId, command)
      .then((response) => {
        setIsLoading(false);
        if (response.data) {
          toast.success("Lesson plan saved successfully");
        } else {
          toast.error("Failed to save lesson plan");
        }
      })
      .catch((error) => {
        setIsLoading(false);
        toast.error("Failed to update lesson plan");
      });
  };

  useEffect(() => {
    setBreadCrumbs(breadcrumbItems());
  }, [lessonPlan.lessonPlanId]);

  useEffect(() => {
    if (lessonPlan.lessonPlanId > 0) {
      getLessonPlanById(lessonPlan.lessonPlanId)
        .then((response) => {
          if (response.data) {
            setSubject({
              label: response.data.subjectName,
              value: response.data.subjectId.toString(),
            });
            setLessonPlan(response.data);
          }
        })
        .catch((error) => {
          setIsLoading(false);
          toast.error("Failed to get lesson plan");
        });
      getLessonPlanAssestsByLessonPlanId(lessonPlan.lessonPlanId)
        .then((response) => {
          setAssets(response.data);
        })
        .catch((error) => {
          setIsLoading(false);
          toast.error("Failed to retrieve lesson plan assets.");
        });
    }
    getSubjects()
      .then((response) => {
        const lessonPlanSubjects: Array<ISelectElement> = response.data
          .filter((s) => {
            return !s.hasLessonPlan;
          })
          .map((lessonPlanSubject: ILessonPlanSubject) => {
            return {
              label: lessonPlanSubject.name,
              value: lessonPlanSubject.subjectId.toString(),
            };
          });
        setSubjects(lessonPlanSubjects);
      })
      .catch((error) => {
        setIsLoading(false);
        toast.error("Failed to retrieve lesson plan subjects");
      });
  }, []);

  return (
    <AllowedTo
      perform={Permissions.lesson_plan_create}
      no={() => <AccessDenied />}
    >
      {isLoading && <Loader />}
      <div className="flex-1">
        <div className=" h-auto min-h-half w-full rounded-bl-3xl bg-site-teal px-8  py-8 pt-0 pb-14 mt-7 ">
          <div className="relative  py-1">
            <div className="overlay-image-main pointer-events-none fixed left-0 top-0 w-screen h-screen z-20"></div>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-light text-gray-900">Lesson plan</h3>
              {lessonPlan && (
                <button
                  onClick={handleAddAssetClick}
                  type="button"
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 rounded-md shadow-xs text-white dark:text-white bg-primary-color hover:bg-secondary-color focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color hover:shadow-lg transsition ease-in-out duration-300"
                >
                  <AddIcon className="w-5"></AddIcon>
                  Add Asset
                </button>
              )}
            </div>
            <Breadcrumb items={breadCrumbs} />
            <div className="pt-6 border-b pb-6">
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-2 sm:col-span-2  ">
                  <label className="text-sm text-gray-700">Subject</label>
                  <Select
                    value={subject}
                    onChange={handleSubjectChange}
                    options={subjects}
                    theme={(theme) => ({
                      ...theme,
                      colors: {
                        ...theme.colors,
                        primary25: "#B3B3B3",
                        primary: "#041E42",
                      },
                    })}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        background:
                          document.getElementsByClassName("dark")?.length > 0
                            ? "#1A1C23"
                            : "white",
                        borderColor:
                          document.getElementsByClassName("dark")?.length > 0
                            ? "#6B7280"
                            : "#D1D5DB",
                        borderWidth: "1px",
                        "&:hover": {
                          borderColor:
                            state.isFocused &&
                            document.getElementsByClassName("dark")?.length > 0
                              ? "#6b7280"
                              : state.isFocused
                              ? "#4c5563"
                              : "",
                        },
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color:
                          document.getElementsByClassName("dark")?.length > 0
                            ? "white"
                            : "#374051",
                      }),
                      input: (base) => ({
                        ...base,
                        color:
                          document.getElementsByClassName("dark")?.length > 0
                            ? "white"
                            : "",
                      }),
                    }}
                    className="text-gray-900 cursor-default select-none relative py-2"
                    isSearchable={true}
                    isDisabled={false}
                  />
                </div>
                <div className="col-span-1 flex items-end">
                  <div className="flex items-center">
                    <input
                      id="isApproved"
                      name="isApproved"
                      type="checkbox"
                      className=" focus:ring-primary-color h-4 w-4 text-primary-color border-gray-300 rounded"
                      onChange={handleIsApprovedChange}
                      checked={lessonPlan.isApproved}
                    />
                    <label className="ml-1.5 py-2 text-sm text-gray-700">
                      Approved
                    </label>
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <button
                  type="submit"
                  className="inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg"
                  onClick={handleSaveClick}
                  disabled={lessonPlan.subjectId === 0}
                >
                  <FilterIcon className="w-3 h-3"></FilterIcon>
                  <span>Save</span>
                </button>
                <button
                  type="button"
                  onClick={handleCancelClick}
                  className="py-1.5 px-4 inline-flex ml-3 items-center text-sm rounded-lg text-gray-700 text-primary-color border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color "
                >
                  <ResetIcon className="w-3 h-3"></ResetIcon>
                  <span>Cancel</span>
                </button>
              </div>
            </div>
            <div id="dataContainer" className="mt-4">
              <div className="flex flex-col mt-1">
                <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                  <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-primary-color">
                          <tr>
                            <th
                              scope="col"
                              className="px-5 py-2 font-light text-sm text-left text-white dark:text-white tracking-wider"
                            >
                              <span className="flex">
                                Position
                                <span>
                                  <CaretDownSolidIcon className="w-5 h-5"></CaretDownSolidIcon>
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-5 py-2 font-light text-sm text-left text-white dark:text-white tracking-wider"
                            >
                              <span className="flex">Title</span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2  font-light text-sm text-left text-white dark:text-white tracking-wider"
                            >
                              <span className="flex">Standard Codes</span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2  font-light text-sm text-left text-white dark:text-white tracking-wider"
                            >
                              <span className="flex">Description</span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2  font-light text-sm text-left text-white dark:text-white tracking-wider"
                            >
                              <span className="flex justify-center items-center">
                                Approved
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2  font-light text-sm text-left text-white dark:text-white tracking-wider"
                            >
                              <span className="flex justify-center items-center">
                                Action
                              </span>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {assets.map((asset, index) => (
                            <tr
                              key={asset.title}
                              className={
                                index % 2 === 0 ? "bg-white" : "bg-gray-100"
                              }
                            >
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                {asset.ordinal}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                {asset.title}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                {asset.standardCodes}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                {asset.description}
                              </td>
                              <td>
                                <div className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white flex justify-center items-center">
                                  {asset.isActive ? (
                                    <ApproveIcon
                                      className={"w-6 text-green-500"}
                                    />
                                  ) : (
                                    <CancelIcon
                                      className={"w-6 text-red-700"}
                                    />
                                  )}
                                </div>
                              </td>
                              <td>
                                <div className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white flex justify-center items-center">
                                  <AllowedTo
                                    perform={Permissions.lesson_plan_create}
                                  >
                                    <EditIcon
                                      onClick={() =>
                                        handleEditAssetclick(
                                          asset.lessonPlanAssetId
                                        )
                                      }
                                      className="w-4 text-xs font-light cursor-pointer hover:text-secondary-color transaction ease-in-out duration-300"
                                    />
                                  </AllowedTo>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AllowedTo>
  );
};
